// React
import React from 'react';

// Cosmos
import { Button, Configuration, Flex, Icon, Text } from '@pega/cosmos-react-core';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { appState } from '../../../../../interfaces/common';
import useLocalTranslation from '../../../../../hooks/useLocalTranslation';

// Styling
const inputTheme = {
  base: {
    shadow: {
      focus: `none`,
    },
  },
  components: {
    'text-area': {
      'min-height': '32px',
    },
  },
};

const InputWithPadding = styled.div(
  ({ theme }) =>
    css`
      padding: 0.5rem;
      border-top: 1px solid ${theme.base.colors.gray.light};
    `,
);

const inputStyle = {
  overflow: 'hidden',
  border: 'none',
  height: '32px',
  paddingLeft: '0.5rem',
  paddingTop: '0.5rem',
};

const BlockedInput = styled.div(
  () =>
    css`
      justify-content: center;
      align-items: center;
      height: 4.75rem;
      opacity: 0.7;
    `,
);

const DismissButtonStyle = styled.button(
  ({ theme }) =>
    css`
      border-bottom: 1px solid ${theme.base.palette['brand-primary']};
      &:hover {
        text-decoration: none;
      }
    `,
);

const BlockedInputContainer = styled.div(
  ({ theme }) =>
    css`
      padding: 0.5rem;
      border-top: 1px solid ${theme.base.colors.gray.light};
      background-color: ${theme.base.colors.gray['extra-light']};
    `,
);

interface ConversationInputProps {
  selectedThemeColor: any;
}

const ConversationInput: React.FC<ConversationInputProps> = ({ selectedThemeColor }) => {
  const { inputTextAreaState, emojiIcon, attachmentIcon } = useSelector((state: appState) => state.chatInfo);
  const translate = useLocalTranslation();

  const disableInput = ['disable_input', 'enable_input_conditional'].includes(inputTextAreaState || 'enable_input');

  const StyledIconButton = {
    color: selectedThemeColor,
    marginInlineStart: 0,
  };
  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 1,
      }}
      item={{
        shrink: 0,
      }}
      as={disableInput ? BlockedInputContainer : InputWithPadding}
    >
      {disableInput ? (
        <Flex as={BlockedInput} style={{ textAlign: 'center' }} container={{ direction: 'column' }}>
          <Text>{translate('disabled_input_msg_menu')}</Text>
          {inputTextAreaState !== 'disable_input' && <Text>{translate('or')}</Text>}
          {inputTextAreaState !== 'disable_input' && (
            <Button variant="link" as={DismissButtonStyle} style={{ textAlign: 'center' }}>
              <Text>{translate('enable_messaging')}</Text>
            </Button>
          )}
        </Flex>
      ) : (
        <>
          <Flex
            container={{
              direction: 'column',
              itemGap: 0.5,
            }}
          >
            <Configuration theme={inputTheme}>
              <Flex style={inputStyle}>
                <Text style={{ fontSize: '12px' }}>{translate('type_message')}</Text>
              </Flex>
            </Configuration>
          </Flex>
          <Flex container={{ itemGap: 0.5, justify: 'between' }}>
            <Flex>
              {emojiIcon && (
                <Button variant="simple" icon title={translate('emoji_picker')} style={StyledIconButton}>
                  <Icon name="face-happy" />
                </Button>
              )}
              {attachmentIcon && (
                <Button variant="simple" icon title={translate('upload_attachment')} style={StyledIconButton}>
                  <Icon name="paper-clip" />
                </Button>
              )}
            </Flex>
            <Flex>
              <Button
                variant="simple"
                title={translate('send_message')}
                icon
                style={StyledIconButton}
                data-testid="send_message"
              >
                <Icon name="send-solid" />
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default ConversationInput;
